import { ErrorBoundary as ErrorBoundaryWrapper, FallbackProps } from "react-error-boundary";
import { LargeScreen, SmallScreen } from "Core/Responsive";
import React, { Suspense, createContext, lazy, use, useMemo } from "react";

import { AppLeftBar } from "../Components/Nav/AppLeftBar/AppLeftBar";
import { AppMenu } from "Components/Nav/AppHeaderMenu/AppMenu";
import { ErrorBoundary } from "Components/Pages/ErrorBoundary";
import { M365PulseToaster } from "Shared/Components/Toast/M365PulseToaster";
import { Outlet } from "react-router";
import { observer } from "mobx-react";
import { useGlobalSetting } from "Store/GlobalSettingStore";
import { useM365PulseSiteLayoutStyle } from "./M365PulseSiteLayout.styles";
import { useRootStore } from "Store/RootStore";
import { useSmallScreen } from "Shared/Hooks";

const LayoutContext = createContext(false);

// The OpsGenie panel is most of the OpsGenie code, so it loads after startup. If its chunk fails, only the panel is missing.
const OpsGenieRightSidePanel = lazy(async () => ({
    default: (await import("Apps/OpsGenie/Components/OpsGenieRightPanel/OpsGenieRightSidePanel")).OpsGenieRightSidePanel
}));

const M365PulseSiteLayoutBase = (props: { children?: React.ReactElement }) => {
    const isSmallScreen = useSmallScreen();
    const toasterId = useRootStore("toasterId");
    const opsGenieStore = useGlobalSetting("opsGenieApp");
    const classes = useM365PulseSiteLayoutStyle({ isSmallScreen, isOpsGeniePanelOpen: opsGenieStore.isRightPanelOpen });
    const appPage = useMemo(() => (props.children ? props.children : <Outlet />), [props.children]);

    return (
        <ErrorBoundaryWrapper fallbackRender={fallbackRender}>
            <LayoutContext.Provider value>
                <a href="#SkipToMainContent" className={classes.skipToMainContent}>
                    Skip to main content
                </a>
                <div className={classes.root}>
                    <SmallScreen>
                        <div className={classes.header} role="navigation">
                            <AppMenu />
                        </div>
                    </SmallScreen>
                    <LargeScreen>
                        <div className={classes.header} role="banner">
                            <AppMenu />
                        </div>
                        <div className={classes.leftBar} role="navigation" aria-label="Primary">
                            <AppLeftBar />
                        </div>
                    </LargeScreen>
                    <div className={classes.appRootMain}>
                        <div id="SkipToMainContent" className={classes.appContent} role="main" data-is-scrollable="true">
                            {appPage}
                        </div>
                        {!props.children && (
                            <ErrorBoundaryWrapper fallback={null}>
                                <Suspense fallback={null}>
                                    <OpsGenieRightSidePanel />
                                </Suspense>
                            </ErrorBoundaryWrapper>
                        )}
                    </div>
                    <M365PulseToaster position="top-end" toasterId={toasterId} />
                </div>
            </LayoutContext.Provider>
        </ErrorBoundaryWrapper>
    );
};

export const M365PulseSiteLayout = observer(M365PulseSiteLayoutBase);

export const useInLayout = () => use(LayoutContext);

const fallbackRender = ({ error }: FallbackProps) => <ErrorBoundary errorProp={error} />;
